import { House, MessageCircle, Briefcase, Zap, Settings } from "lucide-react";
import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { useAppStore } from "@/stores/appStore";
import { useLanguageStore } from "@/stores/languageStore";
import { hapticFeedback } from "@/lib/telegram";

interface NavTab {
  path: string;
  label: string;
  labelAm: string;
  icon: LucideIcon;
}

const NAV_TABS: NavTab[] = [
  { path: "/", label: "Home", labelAm: "ቤት", icon: House },
  { path: "/chats", label: "Inbox", labelAm: "መልዕክት", icon: MessageCircle },
  { path: "/business", label: "Business", labelAm: "ንግድ", icon: Briefcase },
  { path: "/growth", label: "Growth", labelAm: "እድገት", icon: Zap },
  { path: "/settings", label: "Settings", labelAm: "ቅንብር", icon: Settings },
];

const springTransition = {
  type: "spring" as const,
  stiffness: 300,
  damping: 30,
};

function isTabActive(activeRoute: string, tabPath: string): boolean {
  if (tabPath === "/") return activeRoute === "/";
  if (activeRoute === tabPath || activeRoute.startsWith(`${tabPath}/`)) return true;

  if (tabPath === "/chats") {
    return ["/contacts"].some((prefix) =>
      activeRoute === prefix || activeRoute.startsWith(`${prefix}/`),
    );
  }

  if (tabPath === "/business") {
    return ["/subscriptions", "/market"].some((prefix) =>
      activeRoute === prefix || activeRoute.startsWith(`${prefix}/`),
    );
  }

  if (tabPath === "/growth") {
    return ["/automations", "/channels", "/groups", "/intelligence", "/marketing"].some((prefix) =>
      activeRoute === prefix || activeRoute.startsWith(`${prefix}/`),
    );
  }

  if (tabPath === "/settings") {
    return ["/team"].some((prefix) =>
      activeRoute === prefix || activeRoute.startsWith(`${prefix}/`),
    );
  }

  return false;
}

interface NavButtonProps {
  tab: NavTab;
  isActive: boolean;
  onNavigate: (path: string) => void;
}

function NavButton({ tab, isActive, onNavigate }: NavButtonProps) {
  const Icon = tab.icon;
  const language = useLanguageStore((state) => state.language);

  function handleTap() {
    hapticFeedback("selection");
    onNavigate(tab.path);
  }

  return (
    <motion.button
      type="button"
      onClick={handleTap}
      whileTap={{ scale: 0.9 }}
      transition={springTransition}
      className="flex flex-1 flex-col items-center justify-center gap-1 py-2"
      aria-label={tab.label}
      aria-current={isActive ? "page" : undefined}
    >
      <div className="relative">
        <Icon
          className={cn(
            "h-[22px] w-[22px] transition-colors duration-150",
            isActive ? "text-[var(--color-nav-active)]" : "text-[var(--color-nav-inactive)]",
          )}
          strokeWidth={isActive ? 2.2 : 1.8}
        />
        {isActive && (
          <motion.div
            layoutId="nav-indicator"
            className="absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-[var(--color-nav-active)]"
            transition={springTransition}
          />
        )}
      </div>
      <span
        className={cn(
          "text-[10px] font-medium leading-none transition-colors duration-150",
          isActive ? "text-[var(--color-nav-active)]" : "text-[var(--color-nav-label-inactive)]",
        )}
      >
        {language === "am" ? tab.labelAm : tab.label}
      </span>
    </motion.button>
  );
}

interface BottomNavProps {
  onNavigate: (path: string) => void;
}

export function BottomNav({ onNavigate }: BottomNavProps) {
  const activeTab = useAppStore((state) => state.activeTab);

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 flex items-center
                 border-t border-[var(--color-border)]
                 bg-[var(--color-bg)]/95 backdrop-blur-xl"
      style={{
        height: `calc(var(--bottom-nav-height) + var(--safe-area-bottom))`,
        paddingBottom: "var(--safe-area-bottom)",
      }}
    >
      {NAV_TABS.map((tab) => (
        <NavButton
          key={tab.path}
          tab={tab}
          isActive={isTabActive(activeTab, tab.path)}
          onNavigate={onNavigate}
        />
      ))}
    </nav>
  );
}
